import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { loadFavoritesByUser, saveFavoritesByUser } from '@/services/favorites/favorites-storage'
import { type BookingState, INITIAL_BOOKING, mergeBookingState } from './booking-state'

const THEME_STORAGE_KEY = 'partiu-quadra-theme'
const EMPTY_FAVORITE_IDS: string[] = []

export type CoverFilter = 'all' | 'covered' | 'open'
export type SportFilter = string
export type FloorFilter = string
export type AppTheme = 'light' | 'dark'

function resolveInitialTheme(): AppTheme {
  if (typeof window === 'undefined') {
    return 'light'
  }

  const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY)
  if (storedTheme === 'light' || storedTheme === 'dark') {
    return storedTheme
  }

  return 'light'
}

interface AppStore {
  // filters
  sportFilter: SportFilter
  coverFilter: CoverFilter
  floorFilter: FloorFilter
  setSportFilter: (value: SportFilter) => void
  setCoverFilter: (value: CoverFilter) => void
  setFloorFilter: (value: FloorFilter) => void
  resetFilters: () => void

  // venues / booking
  activeVenueId: string | null
  setActiveVenue: (id: string | null) => void
  bookingState: BookingState
  setBooking: (partial: Partial<BookingState>) => void
  resetBooking: () => void
  openBookingModal: (venueId: string, courtId?: string | null) => void
  closeBookingModal: () => void

  // favorite venues
  favoritesByUser: Record<string, string[]>
  getFavoriteVenueIds: () => string[]
  isFavoriteVenue: (venueId: string) => boolean
  toggleFavoriteVenue: (venueId: string) => void
  setFavoriteVenue: (venueId: string, isFavorite: boolean) => void

  // reservations
  activeReservationId: string | null
  setActiveReservation: (id: string | null) => void

  // search
  searchQuery: string
  setSearchQuery: (q: string) => void

  // selected location
  selectedCity: string
  setSelectedCity: (city: string) => void

  // theme
  theme: AppTheme
  setTheme: (theme: AppTheme) => void
}

export const useAppStore = create<AppStore>()(
  devtools(
    (set, get) => ({
      sportFilter: 'all',
      coverFilter: 'all',
      floorFilter: 'all',
      setSportFilter: (value) => set({ sportFilter: value }),
      setCoverFilter: (value) => set({ coverFilter: value }),
      setFloorFilter: (value) => set({ floorFilter: value }),
      resetFilters: () => set({ sportFilter: 'all', coverFilter: 'all', floorFilter: 'all' }),

      activeVenueId: null,
      setActiveVenue: (id) => set({ activeVenueId: id }),

      bookingState: INITIAL_BOOKING,
      setBooking: (partial) =>
        set({ bookingState: mergeBookingState(get().bookingState, partial) }),
      resetBooking: () => set({ bookingState: INITIAL_BOOKING }),
      openBookingModal: (venueId, courtId = null) =>
        set({
          activeVenueId: venueId,
          bookingState: {
            ...INITIAL_BOOKING,
            showModal: true,
            selectedCourtId: courtId,
          },
        }),
      closeBookingModal: () => set({ bookingState: { ...get().bookingState, showModal: false } }),

      favoritesByUser: loadFavoritesByUser(),
      getFavoriteVenueIds: () => {
        const state = get()
        const key = toUserFavoriteKey()
        return state.favoritesByUser[key] ?? EMPTY_FAVORITE_IDS
      },
      isFavoriteVenue: (venueId) => {
        const state = get()
        return state.getFavoriteVenueIds().includes(venueId)
      },
      toggleFavoriteVenue: (venueId) => {
        const state = get()
        const key = toUserFavoriteKey()
        const currentFavorites = state.favoritesByUser[key] ?? []
        const nextFavorites = currentFavorites.includes(venueId)
          ? currentFavorites.filter((id) => id !== venueId)
          : [...currentFavorites, venueId]

        const nextByUser = {
          ...state.favoritesByUser,
          [key]: nextFavorites,
        }

        saveFavoritesByUser(nextByUser)
        set({ favoritesByUser: nextByUser })
      },
      setFavoriteVenue: (venueId, isFavorite) => {
        const state = get()
        const key = toUserFavoriteKey()
        const currentFavorites = state.favoritesByUser[key] ?? []
        const hasFavorite = currentFavorites.includes(venueId)

        if (isFavorite === hasFavorite) {
          return
        }

        const nextFavorites = isFavorite
          ? [...currentFavorites, venueId]
          : currentFavorites.filter((id) => id !== venueId)

        const nextByUser = {
          ...state.favoritesByUser,
          [key]: nextFavorites,
        }

        saveFavoritesByUser(nextByUser)
        set({ favoritesByUser: nextByUser })
      },

      activeReservationId: null,
      setActiveReservation: (id) => set({ activeReservationId: id }),

      searchQuery: '',
      setSearchQuery: (q) => set({ searchQuery: q }),

      selectedCity: 'Detectar localização',
      setSelectedCity: (city) => set({ selectedCity: city }),

      theme: resolveInitialTheme(),
      setTheme: (theme) => {
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(THEME_STORAGE_KEY, theme)
        }
        set({ theme })
      },
    }),
    { name: 'partiu-quadra-store' },
  ),
)

function toUserFavoriteKey(): string {
  return 'guest'
}
